import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, downloadReadersCsv, STORE_LABELS } from "../lib/api";
import { Switch } from "./ui/switch";
import { CopyButton, Metric } from "./Bits";

function Toggles({ book, setBook }) {
  const update = async (patch) => {
    try {
      const { data } = await http.post(`/books/${book.id}/publish`, { published: book.published, hide_from_discover: book.hide_from_discover, ...patch });
      setBook(data);
      toast.success(data.published ? "Your book page is live" : "Book page set to draft");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const link = `${window.location.origin}/b/${book.slug}`;
  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-6 border-t border-[#121210] pt-5">
        <div>
          <p className="font-display text-2xl">Publish book page</p>
          <p className="text-sm text-[#5C584E] mt-1">Anyone with the link can see it, and it appears on the Discover shelf.</p>
        </div>
        <Switch checked={book.published} onCheckedChange={(v) => update({ published: v })} data-testid="page-publish-switch" />
      </div>
      <div className="flex items-start justify-between gap-6 border-t border-[#E5DEC9] pt-5">
        <div>
          <p className="font-display text-2xl">Hide from Discover</p>
          <p className="text-sm text-[#5C584E] mt-1">Keep the page shareable by link only.</p>
        </div>
        <Switch checked={book.hide_from_discover} onCheckedChange={(v) => update({ hide_from_discover: v })} data-testid="page-hide-discover-switch" />
      </div>
      <div className="border-t border-[#E5DEC9] pt-5">
        <p className="label-mono">Share link</p>
        <p className="font-type text-sm mt-2 break-all" data-testid="page-share-link">{link}</p>
        <div className="flex -ml-2.5 mt-2">
          <CopyButton text={link} testid="page-copy-link-button" label="Copy link" />
          <Link to={`/b/${book.slug}`} className="btn-ghost" data-testid="page-open-link"><ExternalLink size={12} /> {book.published ? "Open" : "Preview"}</Link>
        </div>
      </div>
    </div>
  );
}

function Readers({ book }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    http.get("/readers", { params: { book_id: book.id } }).then(({ data }) => setRows(data));
  }, [book.id]);
  return (
    <div className="mt-14" data-testid="page-readers">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl">Readers who joined here</h3>
        <button className="btn-secondary !py-2" onClick={() => downloadReadersCsv(book.id).catch((e) => toast.error(errMsg(e)))} disabled={!rows.length} data-testid="page-export-readers-button"><Download size={12} /> CSV</button>
      </div>
      {rows.length === 0 ? <p className="text-[#8E8879] italic mt-4">No readers yet. Share your link to start your list.</p> : (
        <table className="w-full mt-5 text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E5DEC9]" data-testid={`page-reader-row-${r.email}`}>
                <td className="py-3">{r.email}</td><td className="py-3 text-[#5C584E]">{r.name}</td>
                <td className="py-3 text-right font-type text-xs text-[#8E8879]">{r.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function BookPagePanel({ book, setBook }) {
  const s = book.stats || {};
  const clicks = Object.entries(s.clicks || {});
  return (
    <div className="grid lg:grid-cols-12 gap-12" data-testid="book-page-panel">
      <div className="lg:col-span-5"><Toggles book={book} setBook={setBook} /></div>
      <div className="lg:col-span-7">
        <div className="grid grid-cols-3 gap-6">
          <Metric label="Page views" value={s.views || 0} testid="page-metric-views" />
          <Metric label="From Discover" value={s.discover_views || 0} testid="page-metric-discover" />
          <Metric label="Readers" value={s.signups || 0} testid="page-metric-readers" />
        </div>
        <div className="mt-10">
          <p className="label-mono mb-3">Buy-button clicks by store</p>
          {clicks.length === 0 ? <p className="text-[#8E8879] italic">No clicks yet.</p> : clicks.map(([k, v]) => (
            <div key={k} className="flex justify-between border-t border-[#E5DEC9] py-2.5" data-testid={`page-clicks-${k}`}>
              <span className="font-display text-lg">{STORE_LABELS[k]}</span><span className="font-type">{v}</span>
            </div>
          ))}
        </div>
        <Readers book={book} />
      </div>
    </div>
  );
}
